import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Check, Copy } from "lucide-react";
import { COUNTRY_CONFIG, providerRegions } from "../config/countries";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STRINGS } from "@/i18n";
import { providerDisplayName } from "@/utils/providerConditions";

const t = STRINGS.api;

interface CopyableCodeBlockProps {
  code: string;
  label: string;
}

function CopyableCodeBlock({ code, label }: CopyableCodeBlockProps) {
  const [copyState, setCopyState] = useState<"idle" | "success" | "error">(
    "idle"
  );

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyState("success");
    } catch {
      setCopyState("error");
    }
    window.setTimeout(() => setCopyState("idle"), 2000);
  };

  const copyLabel =
    copyState === "success"
      ? t.copied
      : copyState === "error"
      ? t.copyFailed
      : t.copy;

  return (
    <div className="relative mt-4">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={copyCode}
        className="absolute right-2 top-2 z-10 bg-card/95"
        aria-label={`${copyLabel}: ${label}`}
        aria-live="polite"
      >
        {copyState === "success" ? (
          <Check aria-hidden="true" />
        ) : (
          <Copy aria-hidden="true" />
        )}
        {copyLabel}
      </Button>
      <pre className="max-h-128 overflow-auto rounded-md bg-muted px-4 pb-4 pt-14 text-xs sm:text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function openSection(id: string) {
  const section = document.getElementById(id);
  if (section instanceof HTMLDetailsElement) {
    section.open = true;
  }
  return section;
}

export function ApiPage() {
  const providers = [...COUNTRY_CONFIG.providers].sort((a, b) =>
    providerDisplayName(a).localeCompare(providerDisplayName(b))
  );
  const [selectedProvider, setSelectedProvider] = useState(
    providers[0]?.id ?? ""
  );
  const [selectedInterval, setSelectedInterval] = useState("hourly");
  const [selectedRegion, setSelectedRegion] = useState(
    COUNTRY_CONFIG.defaultRegionId ?? ""
  );
  const [copyState, setCopyState] = useState<"idle" | "success" | "error">(
    "idle"
  );
  const [testState, setTestState] = useState<"idle" | "loading" | "error">(
    "idle"
  );
  const [testResponse, setTestResponse] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const apiUrl = useMemo(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    const query =
      COUNTRY_CONFIG.regions && selectedRegion
        ? `?region=${selectedRegion}`
        : "";
    return `${base}/api/v1/prices/${selectedProvider}/${selectedInterval}${query}`;
  }, [selectedProvider, selectedInterval, selectedRegion]);

  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash) {
      openSection(hash)?.scrollIntoView();
    }
  }, []);

  return (
    <Card>
      <CardHeader>
        <h2 className="text-2xl font-semibold leading-none tracking-tight">
          {t.title}
        </h2>
      </CardHeader>
      <CardContent className="space-y-4 leading-relaxed text-foreground">
        <p>
          {t.intro(COUNTRY_CONFIG.energyTaxLabel)}
        </p>
        {COUNTRY_CONFIG.regions && (
          <p>
            {t.regions(
              COUNTRY_CONFIG.regions.map((r) => r.id).join(", "),
              COUNTRY_CONFIG.defaultRegionId ?? ""
            )}
          </p>
        )}
        <nav
          aria-label={t.onThisPage}
          className="rounded-md border border-border bg-muted/30 p-4"
        >
          <p className="text-sm font-semibold text-foreground">
            {t.onThisPage}
          </p>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm">
            <li>
              <a
                href="#api-url"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {t.buildUrlLink}
              </a>
            </li>
            <li>
              <a
                href="#api-aanbieders"
                onClick={() => openSection("api-aanbieders")}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {t.providersLink}
              </a>
            </li>
            <li>
              <a
                href="#api-home-assistant"
                onClick={() => openSection("api-home-assistant")}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Home Assistant
              </a>
            </li>
            {COUNTRY_CONFIG.evccTemplate && (
              <li>
                <a
                  href="#api-evcc"
                  onClick={() => openSection("api-evcc")}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  evcc.io
                </a>
              </li>
            )}
          </ul>
        </nav>
        <div
          id="api-url"
          className="scroll-mt-4 rounded-md border border-border bg-muted/40 p-4"
        >
          <p className="mb-3 text-sm font-semibold text-foreground">
            {t.buildUrlTitle}
          </p>
          <div className="grid gap-3 sm:max-w-md sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="api-provider" className="text-sm">
                {t.provider}
              </Label>
              <Select
                value={selectedProvider}
                onValueChange={setSelectedProvider}
              >
                <SelectTrigger id="api-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {providerDisplayName(provider)} ({provider.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="api-interval" className="text-sm">
                {t.interval}
              </Label>
              <Select
                value={selectedInterval}
                onValueChange={setSelectedInterval}
              >
                <SelectTrigger id="api-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">{t.quarterly}</SelectItem>
                  <SelectItem value="hourly">{t.hourly}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {COUNTRY_CONFIG.regions && (
              <div className="space-y-1">
                <Label htmlFor="api-region" className="text-sm">
                  {t.region}
                </Label>
                <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                  <SelectTrigger id="api-region">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CONFIG.regions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name} ({region.id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              aria-label={t.composedUrl}
              name="api-url"
              type="text"
              autoComplete="off"
              spellCheck={false}
              readOnly
              value={apiUrl}
              className="font-mono"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                aria-live="polite"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(apiUrl);
                    setCopyState("success");
                    window.setTimeout(() => setCopyState("idle"), 2000);
                  } catch {
                    setCopyState("error");
                    window.setTimeout(() => setCopyState("idle"), 2000);
                  }
                }}
              >
                {copyState === "success"
                  ? t.copied
                  : copyState === "error"
                  ? t.copyFailed
                  : t.copy}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  setTestState("loading");
                  setTestError(null);
                  try {
                    const response = await fetch(apiUrl);
                    if (!response.ok) {
                      throw new Error(`HTTP ${response.status}`);
                    }
                    const json = await response.json();
                    setTestResponse(JSON.stringify(json, null, 2));
                    setTestState("idle");
                  } catch (err) {
                    console.error("API test failed", err);
                    setTestState("error");
                    setTestError(t.fetchFailed);
                  }
                }}
                disabled={testState === "loading"}
              >
                {testState === "loading" ? t.loading : t.tryIt}
              </Button>
            </div>
          </div>
        </div>
        {(testResponse || testError) && (
          <div aria-live="polite">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span>{t.response}</span>
              <Badge variant={testError ? "destructive" : "success"}>
                {testError ? t.error : "OK"}
              </Badge>
            </div>
            <pre className="mt-2 max-h-120 overflow-x-auto overflow-y-auto rounded-md bg-muted p-4 text-xs sm:text-sm">
              {testError ? testError : testResponse}
            </pre>
          </div>
        )}
        <details
          id="api-aanbieders"
          className="mb-6 scroll-mt-4 rounded-md border border-border px-4 py-2"
        >
          <summary className="cursor-pointer touch-manipulation py-2 font-semibold text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            {t.providersSummary(providers.length)}
          </summary>
          <p className="border-t border-border pt-4 text-sm text-muted-foreground">
            Deze lijst is ook op te halen via{" "}
            <span className="font-mono">/api/v1/providers</span>. Die geeft per
            aanbieder de <span className="font-mono">id</span>, de{" "}
            <span className="font-mono">name</span> en, als de aanbieder er een
            heeft, de naam van het product in{" "}
            <span className="font-mono">product</span>
            {COUNTRY_CONFIG.regions && (
              <>
                , plus de <span className="font-mono">regions</span> waarvoor we
                een tariefkaart geverifieerd hebben. Verschilt het product per
                regio, dan staat het per regio in{" "}
                <span className="font-mono">productByRegion</span>
              </>
            )}
            .
          </p>
          <ul className="grid gap-2 py-4 text-sm sm:grid-cols-2">
            {providers.map((provider) => {
              const regions = providerRegions(provider);
              return (
                <li key={provider.id} className="text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {providerDisplayName(provider)}
                  </span>{" "}
                  <span className="font-mono">({provider.id})</span>
                  {COUNTRY_CONFIG.regions && regions && (
                    <span>
                      {" — "}
                      {regions
                        .map(
                          (id) =>
                            COUNTRY_CONFIG.regions?.find((r) => r.id === id)
                              ?.name ?? id
                        )
                        .join(", ")}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
        <div className="mt-4 rounded-md border border-border bg-muted/40 p-4 text-sm">
          <p>{t.missingProvider}</p>
          <div className="mt-3">
            <Button asChild size="sm">
              <Link to="/?correctie=1">{STRINGS.correction.open}</Link>
            </Button>
          </div>
        </div>
        <details
          id="api-home-assistant"
          className="scroll-mt-4 border-t border-border pt-4"
        >
          <summary className="cursor-pointer touch-manipulation py-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <h3 className="inline text-lg font-bold text-foreground">
              {t.haTitle}
            </h3>
          </summary>
          <div className="pb-2 pt-2">
            <p>{t.haIntro}</p>

            <p className="mt-3 text-sm">{t.haExample}</p>
            <p className="mt-4 text-sm font-semibold text-foreground">
              {t.haConfigTitle}
            </p>
            <CopyableCodeBlock
              label={t.haConfigLabel}
              code={`rest:
  - resource: ${apiUrl}
    scan_interval: 1800
    sensor:
      - name: "${t.ha.dataSensor}"
        value_template: "{{ value_json.today[0].price if value_json.today is defined and value_json.today | length > 0 else none }}"
        json_attributes:
          - today
          - tomorrow

template:
  - sensor:
      - name: "${t.ha.todaySensor}"
        state: >-
          {% set now_h = now().isoformat()[:13] %}
          {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
          {% set value = lst | selectattr('t', 'search', now_h) | map(attribute='price') | list %}
          {{ value[0] if value | length > 0 else none }}

      - name: "${t.ha.tomorrowSensor}"
        state: >-
          {% set now_h = (now() + timedelta(days=1)).isoformat()[:13] %}
          {% set lst = state_attr('${t.ha.dataEntity}', 'tomorrow') or [] %}
          {% set value = lst | selectattr('t', 'search', now_h) | map(attribute='price') | list %}
          {{ value[0] if value | length > 0 else none }}

      - name: "${t.ha.lowestTodaySensor}"
        state: >-
          {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
          {% set item = lst | min(attribute='price') if lst | length > 0 else none %}
          {{ item.price if item is not none else none }}
        attributes:
          timestamp: >-
            {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
            {% set item = lst | min(attribute='price') if lst | length > 0 else none %}
            {{ item.t if item is not none else none }}
          local_time: >-
            {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
            {% set item = lst | min(attribute='price') if lst | length > 0 else none %}
            {{ (as_local(as_datetime(item.t))).strftime('%H:%M') if item is not none else none }}

      - name: "${t.ha.highestTodaySensor}"
        state: >-
          {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
          {% set item = lst | max(attribute='price') if lst | length > 0 else none %}
          {{ item.price if item is not none else none }}
        attributes:
          timestamp: >-
            {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
            {% set item = lst | max(attribute='price') if lst | length > 0 else none %}
            {{ item.t if item is not none else none }}
          local_time: >-
            {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
            {% set item = lst | max(attribute='price') if lst | length > 0 else none %}
            {{ (as_local(as_datetime(item.t))).strftime('%H:%M') if item is not none else none }}

      - name: "${t.ha.currentSensor}"
        state: >-
          {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
          {% set now_h = now().strftime('%Y-%m-%dT%H:00') %}
          {% set item = lst | selectattr('t', 'search', now_h) | list %}
          {{ item[0].price if item | length > 0 else none }}

      - name: "${t.ha.currentQuarterSensor}" # ${t.ha.quarterComment}
        state: >-
          {% set step = 15 %}
          {% set slot = now().replace(minute=(now().minute // step) * step, second=0, microsecond=0) %}
          {% set key = slot.strftime('%Y-%m-%dT%H:%M') %}
          {% set lst = state_attr('${t.ha.dataEntity}', 'today') or [] %}
          {% set match = lst | selectattr('t', 'search', key) | list %}
          {{ match[0].price if match | length > 0 else none }}
        attributes:
          slot_start: >-
            {% set step = 15 %}
            {{ now().replace(minute=(now().minute // step) * step, second=0, microsecond=0).isoformat() }}
`}
            />
            <p className="mt-4 text-sm font-semibold text-foreground">
              {t.apexIntro(
                <a
                  href="https://github.com/RomRider/apexcharts-card"
                  className="font-medium text-primary underline underline-offset-2 hover:no-underline"
                >
                  apexcharts-card
                </a>
              )}
            </p>
            <CopyableCodeBlock
              label={t.apexLabel}
              code={`type: custom:apexcharts-card
graph_span: 24h
span:
  start: day
now:
  show: true
  label: ${t.ha.chartNow}
header:
  show: true
  title: ${t.ha.chartTitle}
yaxis:
  - decimals: 2
    min: ~0
    max: "|+0.05|"
series:
  - entity: ${t.ha.dataEntity}
    stroke_width: 2
    name: ${t.ha.chartToday}
    float_precision: 3
    type: line
    curve: stepline
    opacity: 1
    color: ""
    data_generator: |
      return entity.attributes.today.map((item) => [item.t, item.price]);
  - entity: ${t.ha.dataEntity}
    stroke_width: 2
    name: ${t.ha.chartTomorrow}
    float_precision: 3
    type: line
    curve: stepline
    opacity: 0.5
    color: grey
    data_generator: |
      return (entity.attributes.tomorrow || []).map((item) => [item.t, item.price]);`}
            />
          </div>
        </details>
        {COUNTRY_CONFIG.evccTemplate && (
          <details
            id="api-evcc"
            className="scroll-mt-4 border-t border-border pt-4"
          >
            <summary className="cursor-pointer touch-manipulation py-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <h3 className="inline text-lg font-bold text-foreground">
                {t.evccTitle}
              </h3>
            </summary>
            <div className="pb-2 pt-2">
              <p className="text-sm">{t.evccIntro}</p>
              <p className="mt-4 text-sm font-semibold text-foreground">
                {t.evccConfigTitle}
              </p>
              <CopyableCodeBlock
                label={t.evccLabel}
                code={`tariffs:
  grid:
    type: template
    template: ${COUNTRY_CONFIG.evccTemplate}
    provider: # ${t.evccProviderComment}
    price-interval: quarterly # Price interval, [hourly, quarterly]`}
              />
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
